import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { apiFetch, getApiUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { canAccess } from '../../utils/roleUtils';
import { useMessages } from '../Messages';
import { showApiError } from '../../utils/errorUtils';
import './PickListViewer.css';

const PickListViewer = ({ pickListId, onClose }) => {
  const { t } = useTranslation('supervisor');
  const { t: tCommon } = useTranslation('common');
  const { goToSalesOrder } = useAppNavigate();
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useMessages();
  const [pickList, setPickList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pickItems, setPickItems] = useState([]);

  useEffect(() => {
    if (pickListId) {
      fetchPickListDetails();
    }
  }, [pickListId]);

  const fetchPickListDetails = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${pickListId}`));
      
      if (response.ok) {
        const data = await response.json();
        setPickList(data);
        
        // Initialize pick items with current quantities
        if (data.items) {
          setPickItems(data.items.map(item => ({
            ...item,
            picked_quantity: item.picked_quantity || item.planned_quantity
          })));
        }
      } else {
        showError(t('pickListViewer.messages.error'), t('pickListViewer.messages.failedToLoad'));
      }
    } catch (error) {
      console.error('Error fetching pick list:', error);
      showError(t('pickListViewer.messages.error'), t('pickListViewer.messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartPicking = async () => {
    try {
      setProcessing(true);
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${pickListId}/start`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id })
      });

      if (response.ok) {
        showSuccess(t('pickListViewer.messages.success'), t('pickListViewer.messages.pickListStarted'));
        await fetchPickListDetails();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error starting pick list:', error);
      showError(t('pickListViewer.messages.error'), t('pickListViewer.messages.failedToStart'));
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdatePickedQuantity = (itemId, quantity) => {
    setPickItems(prevItems =>
      prevItems.map(item =>
        item.pick_item_id === itemId
          ? { ...item, picked_quantity: parseFloat(quantity) || 0 }
          : item
      )
    );
  };

  const handleRecordPick = async (item) => {
    try {
      const response = await apiFetch(
        getApiUrl(`/api/pick-lists/${pickListId}/items/${item.pick_item_id}/pick`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            picked_quantity: item.picked_quantity,
            picked_by: user.user_id,
            notes: null
          })
        }
      );

      if (response.ok) {
        showSuccess(t('pickListViewer.messages.success'), t('pickListViewer.messages.pickRecorded'));
        await fetchPickListDetails();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error recording pick:', error);
      showError(t('pickListViewer.messages.error'), t('pickListViewer.messages.failedToRecord'));
    }
  };

  const handleCompletePicking = async () => {
    try {
      setProcessing(true);
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${pickListId}/complete`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id })
      });

      if (response.ok) {
        showSuccess(t('pickListViewer.messages.success'), t('pickListViewer.messages.pickListCompleted'));
        await fetchPickListDetails();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error completing pick list:', error);
      showError(t('pickListViewer.messages.error'), t('pickListViewer.messages.failedToComplete'));
    } finally {
      setProcessing(false);
    }
  };

  const handleIssueMaterials = async () => {
    try {
      setProcessing(true);
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${pickListId}/issue`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.user_id,
          delivery_notes: 'Issued to production floor'
        })
      });

      if (response.ok) {
        showSuccess(t('pickListViewer.messages.success'), t('pickListViewer.messages.materialsIssued'));
        await fetchPickListDetails();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error issuing materials:', error);
      showError(t('pickListViewer.messages.error'), t('pickListViewer.messages.failedToIssue'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="loading-state">{t('pickListViewer.messages.loading')}</div>
        </div>
      </div>
    );
  }

  if (!pickList) {
    return null;
  }

  const canStart = pickList.status === 'PENDING';
  const canComplete = pickList.status === 'IN_PROGRESS' && 
    pickItems.every(item => item.status === 'PICKED' || item.status === 'SHORT');
  const canIssue = pickList.status === 'COMPLETED' && !pickList.issued_date && pickList.pick_list_type !== 'SALES_ORDER';
  const canPrepareShipment = pickList.status === 'COMPLETED' && pickList.pick_list_type === 'SALES_ORDER';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content pick-list-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📝 {t('pickListViewer.title')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="pick-list-info">
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">{t('pickListViewer.labels.pickListNumber')}:</span>
              <span className="info-value"><strong>{pickList.pick_list_number}</strong></span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pickListViewer.labels.order')}:</span>
              <span className="info-value">
                <strong>{pickList.po_number || pickList.so_number || '-'}</strong>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pickListViewer.labels.customer')}:</span>
              <span className="info-value">{pickList.customer_name || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pickListViewer.labels.type')}:</span>
              <span className="info-value">{pickList.pick_list_type}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pickListViewer.labels.priority')}:</span>
              <span className={`status-badge ${
                pickList.priority === 'URGENT' ? 'status-error' :
                pickList.priority === 'HIGH' ? 'status-warning' :
                'status-secondary'
              }`}>
                {pickList.priority}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pickListViewer.labels.status')}:</span>
              <span className={`status-badge ${
                pickList.status === 'PENDING' ? 'status-pending' :
                pickList.status === 'IN_PROGRESS' ? 'status-in-progress' :
                pickList.status === 'COMPLETED' ? 'status-success' :
                'status-error'
              }`}>
                {pickList.status}
              </span>
            </div>
          </div>
        </div>

        <div className="modal-body">
          <h3>{t('pickListViewer.sections.itemsToPick')}</h3>
          <div className="pick-items-table">
            <table className="warehouse-table">
              <thead>
                <tr>
                  <th>{t('pickListViewer.headers.itemCode')}</th>
                  <th>{t('pickListViewer.headers.itemName')}</th>
                  <th>{t('pickListViewer.headers.lot', 'Lot')}</th>
                  <th>{t('pickListViewer.headers.fromLocation')}</th>
                  <th>{t('pickListViewer.headers.toLocation')}</th>
                  <th>{t('pickListViewer.headers.plannedQty')}</th>
                  <th>{t('pickListViewer.headers.pickedQty')}</th>
                  <th>{t('pickListViewer.headers.status')}</th>
                  {pickList.status === 'IN_PROGRESS' && <th>{t('pickListViewer.headers.action')}</th>}
                </tr>
              </thead>
              <tbody>
                {pickItems.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                      {t('pickListViewer.messages.noItems')}
                    </td>
                  </tr>
                ) : (
                  pickItems.map(item => {
                    const currentItem = pickItems.find(pi => pi.pick_item_id === item.pick_item_id);
                    return (
                      <tr key={item.pick_item_id}>
                        <td><strong>{item.item_code}</strong></td>
                        <td>{item.item_name}</td>
                        <td>{item.lot_number || '-'}</td>
                        <td>{item.from_location || '-'}</td>
                        <td>{item.to_location || 'STAGING'}</td>
                        <td>{parseFloat(item.planned_quantity).toFixed(2)}</td>
                        <td>
                          {pickList.status === 'IN_PROGRESS' ? (
                            <input
                              type="number"
                              value={currentItem?.picked_quantity || ''}
                              onChange={(e) => handleUpdatePickedQuantity(item.pick_item_id, e.target.value)}
                              step="0.01"
                              min="0"
                              max={item.planned_quantity}
                              style={{ width: '100px' }}
                            />
                          ) : (
                            parseFloat(item.picked_quantity || 0).toFixed(2)
                          )}
                        </td>
                        <td>
                          <span className={`status-badge ${
                            item.status === 'PICKED' ? 'status-success' :
                            item.status === 'SHORT' ? 'status-warning' :
                            'status-secondary'
                          }`}>
                            {item.status || 'PENDING'}
                          </span>
                        </td>
                        {pickList.status === 'IN_PROGRESS' && (
                          <td>
                            <button
                              className="btn-action btn-primary"
                              onClick={() => handleRecordPick(currentItem)}
                              disabled={!currentItem?.picked_quantity}
                            >
                              ✓ {t('pickListViewer.buttons.pick')}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="modal-footer">
          {canStart && (
            <button
              className="btn-action btn-success"
              onClick={handleStartPicking}
              disabled={processing}
            >
              {processing ? t('pickListViewer.buttons.starting') : `▶️ ${t('pickListViewer.buttons.startPicking')}`}
            </button>
          )}
          {canComplete && (
            <button
              className="btn-action btn-success"
              onClick={handleCompletePicking}
              disabled={processing}
            >
              {processing ? t('pickListViewer.buttons.completing') : `✓ ${t('pickListViewer.buttons.completePickList')}`}
            </button>
          )}
          {canIssue && (
            <button
              className="btn-action btn-warning"
              onClick={handleIssueMaterials}
              disabled={processing}
            >
              {processing ? t('pickListViewer.buttons.issuing') : `🚚 ${t('pickListViewer.buttons.issueMaterials')}`}
            </button>
          )}
          {canPrepareShipment && (
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.9em' }}>
                {t('pickListViewer.messages.readyForShipment')}
              </span>
              {/* Warehouse users cannot open Sales (the route guard would send them home); they ship from Shipping */}
              {canAccess(user, 'sales') && (
                <button
                  className="btn-action btn-primary"
                  onClick={() => goToSalesOrder(pickList.sales_order_id)}
                >
                  📦 {t('pickListViewer.buttons.goToSalesOrder')}
                </button>
              )}
            </div>
          )}
          <button className="btn-action btn-secondary" onClick={onClose}>
            {t('pickListViewer.buttons.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PickListViewer;